import { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";

import { Toaster } from "react-hot-toast";
import api from "./api/axios";

// Employee Pages
import EmployeeLogin from "./pages/employee/Login";
import EmployeeNavbar from "./components/layout/EmployeeNavbar";
// Routes
import EmployeeRoute from "./routes/EmployeeRoute";

// Contexts
import { AuthProvider } from "./context/AuthContext";
import { useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";

import SetPassword from "./pages/employee/SetPassword";
import ResetPassword from "./pages/employee/ResetPassword";
import EmployeeDashboard from "./pages/employee/Dashboard";
import EmployeeAttendance from "./pages/employee/Attendance";
import EmployeeEod from "./pages/employee/Eod";
import EmployeeTasks from "./pages/employee/Tasks";
import EmployeeQuotations from "./pages/employee/Quotations";
import EmployeeAnnouncements from "./pages/employee/Announcements";
import EmployeeComplaints from "./pages/employee/Complaints";
import EmployeeOffboarding from "./pages/employee/Offboarding";
import EmployeePolicies from "./pages/employee/Policies";
import LunchAlerts from "./pages/employee/LunchAlerts";
import TeamAttendance from "./pages/employee/TeamAttendance";
import TeamEod from "./pages/employee/TeamEod";
import TeamLeave from "./pages/employee/TeamLeave";
import Leave from "./pages/employee/Leave";
import Expenses from "./pages/employee/Expenses";

const writeEmployeePageCache = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify({ data, savedAt: Date.now() }));
  } catch {
    // Page-level cache is only for speed; the live request can still fill the UI.
  }
};

const prefetchEmployeeData = async (employee) => {
  const id = employee?._id || employee?.id;
  if (!id || !localStorage.getItem("employee_token")) return;

  const requests = [
    api.get("/api/employee/dashboard-summary").then((res) => {
      writeEmployeePageCache(`employee_dashboard_summary_${id}`, res.data);
    }),
    api.get("/api/employee/attendance/summary", { cache: false, params: { limit: 30, _t: Date.now() } }).then((res) => {
      writeEmployeePageCache(`employee_attendance_summary_${id}`, res.data);
    }),
    Promise.all([
      api.get("/api/employee/leave/my", { cache: false, params: { limit: 370, _t: Date.now() } }),
      api.get("/api/employee/attendance/summary", { cache: false, params: { limit: 370, _t: Date.now() } }),
    ]).then(([leavesRes, attendanceRes]) => {
      const attendanceData = attendanceRes.data;
      localStorage.setItem(
        `employee_corporate_calendar_${id}`,
        JSON.stringify({
          leaves: Array.isArray(leavesRes.data) ? leavesRes.data : [],
          attendance: Array.isArray(attendanceData) ? attendanceData : Array.isArray(attendanceData?.history) ? attendanceData.history : [],
          savedAt: Date.now(),
        }),
      );
    }),
    api.get("/api/employee/leave/my", { params: { limit: 60 } }).then((res) => {
      writeEmployeePageCache(`employee_leave_history_${id}`, Array.isArray(res.data) ? res.data : []);
    }),
    api.get("/api/employee/expenses/my", { params: { limit: 8 } }).then((res) => {
      writeEmployeePageCache(`employee_expense_claims_${id}`, Array.isArray(res.data) ? res.data : []);
    }),
    api.get("/api/employee/tasks", { cache: false, params: { limit: 40, _t: Date.now() } }).then((res) => {
      writeEmployeePageCache(`employee_tasks_${id}`, Array.isArray(res.data) ? res.data : []);
    }),
    api.get("/api/employee/announcements", { cache: false, params: { limit: 20, _t: Date.now() } }).then((res) => {
      writeEmployeePageCache(`employee_announcements_${id}`, Array.isArray(res.data) ? res.data : []);
    }),
    api.get("/api/employee/complaints", { params: { limit: 80 } }).then((res) => {
      writeEmployeePageCache(`employee_complaints_${id}`, Array.isArray(res.data) ? res.data : []);
    }),
    api.get("/api/employee/offboarding", { cache: false, params: { limit: 30 } }).then((res) => {
      writeEmployeePageCache(`employee_offboarding_${id}`, Array.isArray(res.data) ? res.data : []);
    }),
    api.get("/api/employee/policies", { cache: false }).then((res) => {
      writeEmployeePageCache(`employee_policies_${id}`, Array.isArray(res.data) ? res.data : []);
    }),
  ];

  await Promise.allSettled(requests);
};

const HomeRedirect = () => {
  const target = localStorage.getItem("employee_token") ? "/dashboard" : "/login";
  return <Navigate to={target} replace />;
};

const normalizeTeamName = (value = "") => {
  const team = String(value || "").trim().toLowerCase().replace(/[\s_-]+/g, " ");
  if (team === "tele marketing" || team === "telemarketing") return "telemarketing";
  if (team === "administrator") return "admin";
  return team;
};

const canAccessQuotations = (employee = {}) => {
  const team = normalizeTeamName(employee?.team);
  const role = normalizeTeamName(employee?.role);
  return team.includes("manufactur") || team === "telemarketing" || team === "admin" || role === "admin";
};

const QuotationAccessPage = () => {
  const { employee } = useAuth();
  if (!canAccessQuotations(employee)) return <Navigate to="/dashboard" replace />;
  return <EmployeeQuotations />;
};

const ProtectedPage = ({ children }) => (
  <EmployeeRoute>
    {children}
  </EmployeeRoute>
);

const EmployeeLayout = () => {
  const { employee } = useAuth();

  useEffect(() => {
    if (!employee) return undefined;

    const timeoutId = window.setTimeout(() => {
      prefetchEmployeeData(employee);
    }, 150);
    return () => window.clearTimeout(timeoutId);
  }, [employee]);

  return (
    <ProtectedPage>
      <EmployeeNavbar />
      <Outlet />
    </ProtectedPage>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 3000,
              style: {
                background: "#1e1e1e",
                color: "#fff",
                border: "1px solid #2a2a2a",
              },
            }}
          />

          <Routes>
              {/* Default */}
              <Route path="/" element={<HomeRedirect />} />

              {/* Employee */}
              <Route path="/login" element={<EmployeeLogin />} />
              <Route path="/set-password" element={<SetPassword />} />
              <Route path="/set-password/:token" element={<SetPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />

              <Route
                element={<EmployeeLayout />}
              >
                <Route path="/dashboard" element={<EmployeeDashboard />} />
                <Route path="/employee" element={<Navigate to="/dashboard" replace />} />
                <Route path="/employee/dashboard" element={<Navigate to="/dashboard" replace />} />

                <Route path="/attendance" element={<EmployeeAttendance />} />
                <Route path="/employee/attendance" element={<Navigate to="/attendance" replace />} />

                <Route path="/team/attendance" element={<TeamAttendance />} />
                <Route path="/employee/team/attendance" element={<Navigate to="/team/attendance" replace />} />

                <Route path="/team/eod" element={<TeamEod />} />
                <Route path="/employee/team/eod" element={<Navigate to="/team/eod" replace />} />

                <Route path="/team/leave" element={<TeamLeave />} />
                <Route path="/employee/team/leave" element={<Navigate to="/team/leave" replace />} />

                <Route path="/team/tasks" element={<Navigate to="/tasks" replace />} />
                <Route path="/employee/team/tasks" element={<Navigate to="/tasks" replace />} />

                <Route path="/leave" element={<Leave />} />
                <Route path="/leaves" element={<Navigate to="/leave" replace />} />
                <Route path="/employee/leave" element={<Navigate to="/leave" replace />} />
                <Route path="/employee/leaves" element={<Navigate to="/leave" replace />} />

                <Route path="/expenses" element={<Expenses />} />
                <Route path="/employee/expenses" element={<Navigate to="/expenses" replace />} />

                <Route path="/eod" element={<EmployeeEod />} />
                <Route path="/employee/eod" element={<Navigate to="/eod" replace />} />

                <Route path="/tasks" element={<EmployeeTasks />} />
                <Route path="/employee/tasks" element={<Navigate to="/tasks" replace />} />

                <Route path="/quotations" element={<QuotationAccessPage />} />
                <Route path="/employee/quotations" element={<Navigate to="/quotations" replace />} />

                <Route path="/announcements" element={<EmployeeAnnouncements />} />
                <Route path="/employee/announcements" element={<Navigate to="/announcements" replace />} />

                <Route path="/complaints" element={<EmployeeComplaints />} />
                <Route path="/employee/complaints" element={<Navigate to="/complaints" replace />} />

                <Route path="/offboarding" element={<EmployeeOffboarding />} />
                <Route path="/employee/offboarding" element={<Navigate to="/offboarding" replace />} />

                <Route path="/policies" element={<EmployeePolicies />} />
                <Route path="/employee/policies" element={<Navigate to="/policies" replace />} />

                <Route path="/lunch-alerts" element={<LunchAlerts />} />
                <Route path="/employee/lunch-alerts" element={<Navigate to="/lunch-alerts" replace />} />

              </Route>

              {/* Fallback */}
              <Route path="*" element={<HomeRedirect />} />
            </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
